import React from "react";
import RedRibbonBanner from "./ui/RedRibbonBanner";

interface UniversalTableProps {
  title?: string;
  description?: React.ReactNode;
  headers: string[];
  rows: (string | React.ReactNode)[][];
  ribbonTitle?: string;
  ribbonContent?: React.ReactNode;
  columnWidths?: string[];
  greenColumns?: number[]; // Column indexes to make text green (0-based)
  tableWidth?:
    | "full"
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl";
}

export default function UniversalTable({
  title,
  description,
  headers,
  rows,
  ribbonTitle,
  ribbonContent,
  columnWidths,
  greenColumns = [],
  tableWidth = "full",
}: UniversalTableProps) {
  const widthClasses = {
    full: "max-w-full",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
  };

  return (
    <div className="w-full">
      {(title || description) && (
        <div className="my-4 text-center">
          {title && (
            <h3 className="text-xl lg:text-2xl font-semibold text-[#272d57]">
              {title}
            </h3>
          )}

          {description && (
            <p className="mt-3 mx-auto max-w-3xl text-sm sm:text-base md:text-lg leading-relaxed text-gray-700">
              {description}
            </p>
          )}
        </div>
      )}

      <div className={`mx-auto w-full ${widthClasses[tableWidth]}`}>
        <div className="overflow-x-auto rounded-lg">
          <table className="w-full border-collapse border border-[#ccc]/40 text-sm md:text-base">
            {columnWidths && (
              <colgroup>
                {columnWidths.map((width, index) => (
                  <col key={index} className={width} />
                ))}
              </colgroup>
            )}

            <thead>
              <tr>
                {headers.map((header, index) => (
                  <th
                    key={index}
                    className="border border-[#ccc]/40 px-4 md:px-6 py-4 text-left font-bold text-[#d53333]"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="bg-white">
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => {
                    const isGreenColumn =
                      greenColumns.includes(cellIndex);

                    return (
                      <td
                        key={cellIndex}
                        className={`border border-[#ccc]/40 px-4 md:px-6 py-4 align-top
                          ${
                            cellIndex === 0
                              ? "font-semibold text-[#272d57]"
                              : ""
                          }
                          ${
                            isGreenColumn
                              ? "font-medium text-green-600"
                              : ""
                          }
                        `}
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {ribbonTitle && ribbonContent && (
        <div className="my-8">
          <RedRibbonBanner title={ribbonTitle}>
            {ribbonContent}
          </RedRibbonBanner>
        </div>
      )}
    </div>
  );
}